/**
 * LA FICHE QU'ON VIENT DE CREER NE REDEVIENT PAS « INTROUVABLE » (28/10/2026).
 *
 * Regles pures du moteur d'ecran (`useScreen.ts`), sans React, pour qu'un test
 * rejoue la sequence qui clignotait : creation, liste rechargee qui ne contient
 * pas encore la fiche, liste qui la contient. Le detail : `docs/socle/titre-modele-lien.md`.
 *
 * LA CAUSE. La fiche ouverte se cherche, dans cet ordre, dans la liste puis dans
 * `solo` (la fiche lue seule). Deux regles lui faisaient perdre les deux :
 *  - des que la liste CONTENAIT la fiche, `solo` etait vide ; quand un second
 *    rechargement (fiches liees d'un modele, messages systeme) la retirait un
 *    instant, il ne restait rien ;
 *  - la relecture qui comble ce trou ne part pas tant qu'un rechargement est en
 *    vol, et chaque rechargement la repousse : 2 a 4 secondes sans fiche, donc
 *    « introuvable » (l'etat se montre des que la liste, elle, n'est pas vide).
 *
 * LA REGLE. Une fiche creee garde sa valeur lue (`solo`) tant qu'elle reste
 * ouverte, que la liste la contienne ou non. « Introuvable » ne se dit que sur
 * une lecture qui rend « absente ».
 */

/** Ce que l'effet de la fiche seule doit faire de `solo`. */
export type DecisionSolo = 'liberer' | 'garder' | 'attendre' | 'lire';

export interface EtatSolo {
  /** Le mode Modeles est allume : on ne lit jamais une fiche a froid. */
  modelesMode: boolean;
  /** La fiche ouverte, ou `null`. */
  openId: string | null;
  /** L'id du brouillon (`~nouveau`), qui n'est l'id d'aucun document. */
  brouillonId: string;
  /** La fiche ouverte est dans la liste chargee. */
  openIsLoaded: boolean;
  /** Un rechargement de la liste est en vol. */
  inFlight: boolean;
  /** L'id de la fiche lue seule, ou `null`. */
  soloId: string | null;
  /** L'id de la fiche qu'on vient de creer et qui est encore ouverte, ou `null`. */
  creeeId: string | null;
}

/**
 * - `liberer` : `solo` ne sert plus (rien d'ouvert, brouillon, mode Modeles, ou
 *   fiche dans la liste et pas fraichement creee) : on le vide ;
 * - `garder` : `solo` porte deja la fiche ouverte, ou la fiche creee reste
 *   couverte par sa valeur meme quand la liste la contient ;
 * - `attendre` : un rechargement est en vol, on ne lit pas pendant ;
 * - `lire` : la fiche est absente de la liste et de `solo` : on la lit.
 */
export function decisionSolo(etat: EtatSolo): DecisionSolo {
  if (etat.modelesMode || etat.openId == null || etat.openId === etat.brouillonId) return 'liberer';
  if (etat.openIsLoaded) return etat.creeeId === etat.openId ? 'garder' : 'liberer';
  if (etat.inFlight) return 'attendre';
  if (etat.soloId === etat.openId) return 'garder';
  return 'lire';
}

/**
 * L'id de la fiche creee survit tant qu'elle est celle qu'on regarde. Ni autre
 * fiche, ni plus de fiche : il tombe. Le brouillon ne le fait pas tomber, il
 * precede la creation dans le meme geste.
 */
export function creeeApresOuverture(
  creeeId: string | null,
  openId: string | null,
  brouillonId: string,
): string | null {
  if (creeeId == null) return null;
  return openId === creeeId || openId === brouillonId ? creeeId : null;
}

/**
 * Le moteur doit-il dire « chargement » plutot que « introuvable » ? Oui tant
 * que la lecture de la fiche creee n'est pas revenue et que rien ne l'affiche :
 * la liste peut etre pleine d'autres fiches, ce n'est pas elle qui decide.
 */
export function creationEnLecture(
  enAttenteId: string | null,
  openId: string | null,
  ligneOuverte: unknown,
): boolean {
  return enAttenteId != null && enAttenteId === openId && ligneOuverte == null;
}
